import { z } from "zod";

export interface SimulatorBridgeCommand {
  type: "SIMULATOR_COMMAND";
  appId: string;
  commandType: string;
  capability: string;
  payload: Record<string, unknown>;
  requestId?: string;
  attemptId?: string | number;
}

export interface SimulatorBridgeCommandResult {
  type: "SIMULATOR_COMMAND_RESULT";
  requestId?: string;
  ok: boolean;
  commandId?: string;
  result?: unknown;
  error?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function normalizeBridgePayload(value: unknown): Record<string, unknown> {
  if (!isRecord(value)) {
    return {};
  }

  return { ...value };
}

const simulatorBridgeCommandSchema = z.object({
  type: z.literal("SIMULATOR_COMMAND"),
  appId: z.string().trim().min(1),
  commandType: z.string().trim().min(1),
  capability: z.string().trim().min(1),
  payload: z.preprocess((value) => normalizeBridgePayload(value), z.record(z.unknown())),
  requestId: z.string().trim().min(1).optional(),
  attemptId: z.union([z.string().trim().min(1), z.number().int().positive()]).optional()
});

export function extractBridgeRequestId(value: unknown): string | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  return typeof value.requestId === "string" && value.requestId.trim().length > 0
    ? value.requestId.trim()
    : undefined;
}

export function validateSimulatorBridgeCommand(value: unknown) {
  return simulatorBridgeCommandSchema.safeParse(value);
}

export function createSimulatorBridgeErrorResult(
  error: string,
  requestId?: string
): SimulatorBridgeCommandResult {
  return {
    type: "SIMULATOR_COMMAND_RESULT",
    requestId,
    ok: false,
    error
  };
}

export function createSimulatorBridgeSuccessResult(input: {
  requestId?: string;
  commandId?: string;
  result?: unknown;
}): SimulatorBridgeCommandResult {
  return {
    type: "SIMULATOR_COMMAND_RESULT",
    requestId: input.requestId,
    ok: true,
    commandId: input.commandId,
    result: input.result
  };
}

export function sanitizeSimulatorBridgeResponse(value: unknown): {
  commandId?: string;
  result?: Record<string, unknown>;
} {
  if (!isRecord(value)) {
    return {};
  }

  const command = isRecord(value.command) ? value.command : null;
  const event = isRecord(value.event) ? value.event : null;
  const affectedCapabilities = Array.isArray(value.affectedCapabilities)
    ? value.affectedCapabilities.filter((item): item is string => typeof item === "string")
    : [];

  const summary: Record<string, unknown> = {
    accepted: value.accepted === true,
    affectedCapabilities
  };

  if (event && typeof event.type === "string") {
    summary.eventType = event.type;
  }

  if (event && typeof event.summary === "string") {
    summary.eventSummary = event.summary;
  }

  return {
    commandId: command && typeof command.id === "string" ? command.id : undefined,
    result: summary
  };
}
